import React, { useState } from "react";
import IconComponent from "../../../../components/Icon/IconComponent";
import { fontSize } from "../../../../utils/constants/UI";
import styles from "./timelineRow.module.css";

// Backends often pre-format a friendly date like "5/29/2026, 9:26:00 AM".
// `new Date(...)` parses this fine on V8/WebKit, but `formatDate` below was
// trying to coerce it through a non-ISO path and failing. We add an
// explicit parser for that exact format so the timeline date column reads
// it correctly.
const parseShowDateTime = (s) => {
  if (typeof s !== "string") return null;
  // M/D/YYYY[, H:MM[:SS] AM|PM]
  const m = s
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?)?$/);
  if (!m) return null;
  const [, mm, dd, yyyy, hh = "0", mi = "0", ss = "0", ampm] = m;
  let h = parseInt(hh, 10);
  if (ampm) {
    const isPM = /pm/i.test(ampm);
    if (isPM && h < 12) h += 12;
    if (!isPM && h === 12) h = 0;
  }
  const d = new Date(
    parseInt(yyyy, 10),
    parseInt(mm, 10) - 1,
    parseInt(dd, 10),
    h,
    parseInt(mi, 10),
    parseInt(ss, 10),
  );
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (input) => {
  // Empty input means "this row has no meaningful date" (e.g. pending
  // campaign rows that haven't been scheduled yet). Return empty strings so
  // the date column renders blank instead of "--".
  if (input === null || input === undefined || input === "")
    return { line1: "", line2: "" };
  let d;
  if (typeof input === "number") {
    d = new Date(input);
  } else if (typeof input === "string") {
    const asNum = Number(input);
    if (!Number.isNaN(asNum) && String(asNum) === input) {
      d = new Date(asNum);
    } else {
      // Try the backend's "M/D/YYYY, H:MM:SS AM" pattern first; fall back
      // to the generic ISO-ish parser. Without the explicit parser, the
      // browser's Date constructor would coerce the locale string into NaN
      // and the row would render blank.
      d = parseShowDateTime(input) || new Date(input.replace(" ", "T"));
    }
  } else {
    d = new Date(input);
  }
  if (!d || isNaN(d.getTime())) return { line1: "", line2: "" };
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const line1 = `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const line2 = `${String(h).padStart(2, "0")}:${m} ${ampm}`;
  return { line1, line2 };
};

const TimelineRow = ({
  iconLabel,
  iconColor = "#475569",
  iconBg = "#ffffff",
  iconBorder = "#e5e7eb",
  title,
  chip,
  typeChip,
  subtitle,
  date,
  detail,
  // Optional render-slot for a row-specific action (e.g. an Unenroll button
  // on a sales-sequence-started row). Sits inline at the end of the title
  // line, after the chips. Pass any node.
  trailingAction = null,
  defaultExpanded = false,
  detailToggleLabel = "View details",
  // When true, suppress the clock-time line in the date column but keep the
  // date. Used for Marketing Campaign rows whose sends are randomized, so the
  // displayed time is misleading. Defaults false → every existing caller is
  // unchanged unless it opts in.
  hideTime = false,
  // True when a day header directly above this row already states the date, so the
  // row would otherwise print it a second time. The time still shows.
  hideDate = false,
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  const { line1, line2 } = formatDate(date);

  return (
    <div className={styles.row}>
      <div className={styles.iconWrap}>
        <span
          className={styles.iconCircle}
          style={{ background: iconBg, borderColor: iconBorder }}
        >
          <IconComponent
            iconLabel={iconLabel}
            color={iconColor}
            fontSize={fontSize.MD}
          />
        </span>
      </div>

      <div className={styles.content}>
        <div className={styles.titleLine}>
          {title && <span className={styles.title}>{title}</span>}
          {chip && (
            <span
              className={styles.chip}
              style={{
                background: chip.bg || "#f1f5f9",
                color: chip.color || "#334155",
                borderColor: chip.border || "transparent",
              }}
            >
              {chip.label}
            </span>
          )}
          {typeChip && (
            <span
              className={styles.chip}
              style={{
                background: typeChip.bg || "#eef2ff",
                color: typeChip.color || "#4338ca",
                borderColor: typeChip.border || "#c7d2fe",
              }}
            >
              {typeChip.label}
            </span>
          )}
          {trailingAction && (
            <span className={styles.trailingAction}>{trailingAction}</span>
          )}
        </div>
        {/*
          Subtitle and the expand toggle share one line. On its own line the
          toggle added a third stacked line to almost every row, which is what
          made the feed read as tall and airy with uneven gaps between rows.
        */}
        {(subtitle || detail) && (
          <div className={styles.subtitleLine}>
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            {detail && (
              <button
                type="button"
                className={styles.detailToggle}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? "Hide details" : detailToggleLabel}
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.dateCol}>
        {line1 && !hideDate && <div className={styles.dateLine1}>{line1}</div>}
        {line2 && !hideTime && <div className={styles.dateLine2}>{line2}</div>}
      </div>

      {/*
        Expanded detail lives as a grid-spanning row of its own so it can
        use the full timeline width instead of being squeezed inside the
        middle .content column. Without this, wide payloads (the meeting
        attendee/date/time/duration/timezone strip, AI-generated call
        descriptions, etc.) get cut off and dropdowns collide.
      */}
      {detail && open && (
        <div className={styles.detailWrap}>{detail}</div>
      )}
    </div>
  );
};

export { formatDate };
export default TimelineRow;
